import { useState, useRef, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { Download, TrendingUp, ChevronDown, Mail, CreditCard, Loader2, Settings, RefreshCw, FileSignature, AlertTriangle, Info, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { usePaymentDetails, useDownloadInvoice, useDownloadSepaMandate } from '@/features/contracts/hooks/usePaymentDetails';
import { useInvoicePreview, useConfirmRegenerate } from '@/features/contracts/hooks/useRegenerateMonthlyInvoice';
import { formatPrice } from '@/features/contracts/utils/contractFormatter';
import { contractApi } from '@/features/contracts/api/contractApi';
import {
  PAYMENT_STATUS_LABELS,
  PENDING_STATUSES,
  MANDATE_AVAILABLE_STATUSES,
} from '@/features/contracts/constants/payment-status';
import { DirectPaymentModal } from '@/features/contracts/components/DirectPaymentModal';
import { RegenerateInvoiceModal } from '@/features/contracts/components/RegenerateInvoiceModal';
import { PostponeFirstChargeModal } from '@/features/contracts/components/PostponeFirstChargeModal';
import { useFirstChargePostponePreview, useConfirmFirstChargePostpone } from '@/features/contracts/hooks/useFirstChargePostpone';
import { SendNotificationConfirmDialog } from '@/features/contracts/components/SendNotificationConfirmDialog';
import { ContractDisputesBlock } from '@/features/contracts/components/ContractDisputesBlock';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { useQueryClient } from '@tanstack/react-query';

interface PaymentDetailsCardProps {
  contractId: number;
  contractLabel?: string;
  signatureType?: number;
  societyId?: string;
}

// PAYMENT_STATUS_LABELS / PENDING_STATUSES / MANDATE_AVAILABLE_STATUSES :
// désormais partagés (voir '@/features/contracts/constants/payment-status').

/**
 * Messages affichés quand le contrat est en mode « factures Stripe » mais que celles-ci
 * n'ont pas pu être récupérées. Volontairement explicites : l'ancien comportement
 * (repli muet sur l'échéancier) rendait l'incident indétectable côté vendeur.
 */
const STRIPE_INVOICES_ERROR_LABELS: Record<string, string> = {
  NO_STRIPE_ACCOUNT: "Aucun compte Stripe n'est rattaché à la société de ce contrat. Contactez le support.",
  NOT_A_SUBSCRIPTION: "Le paiement de ce contrat n'est pas rattaché à un abonnement Stripe exploitable. Contactez le support.",
  SUBSCRIPTION_NOT_YET_CREATED: 'Le mandat de prélèvement n\'est pas encore confirmé. Les factures apparaîtront après le premier prélèvement.',
  NO_PAID_INVOICE: 'Aucune facture réglée n\'est encore disponible chez Stripe pour ce contrat. Les prélèvements SEPA en cours de règlement apparaîtront une fois encaissés.',
  CASH_INVOICE_UNAVAILABLE: 'Le paiement a bien été encaissé, mais sa facture Stripe est introuvable. Contactez le support : le justificatif existe et doit être récupéré côté Stripe.',
  STRIPE_ERROR: 'La récupération des factures auprès de Stripe a échoué. Réessayez dans quelques instants ; si le problème persiste, contactez le support.',
};

/**
 * Causes qui ne sont PAS des incidents : le contrat n'a simplement pas encore produit de
 * facture. Affichées en information neutre, jamais en alerte — sans quoi chaque contrat
 * neuf déclencherait un ticket support.
 */
const NON_ALARMING_ERRORS = new Set(['SUBSCRIPTION_NOT_YET_CREATED', 'NO_PAID_INVOICE']);

/**
 * Statuts pour lesquels il n'y a RIEN à dire sur les factures — ni maintenant, ni plus tard.
 *
 * Deux familles :
 *  - le contrat n'a pas encore été prélevé (`initialized`, `ongoing`) : annoncer une
 *    indisponibilité sur un contrat neuf générerait un ticket support à chaque vente ;
 *  - le contrat est dans un état terminal : plus aucune facture ne sera produite, donc
 *    afficher « les factures apparaîtront après le premier prélèvement » serait un mensonge.
 *    Cas mesuré en base : 19 contrats en mode Stripe, encore au SetupIntent et annulés —
 *    un abonnement n'y a jamais existé, donc aucune facture n'y a jamais existé non plus.
 *
 * ⚠️ Ce garde ne s'applique QUE dans la branche « aucune facture à montrer ». Un contrat
 * annulé APRÈS plusieurs prélèvements réussis a de vraies factures Stripe : il passe par le
 * dropdown et n'est pas concerné. Le vendeur doit continuer à pouvoir les lettrer.
 */
const NOTHING_TO_SHOW_STATUSES = new Set([
  'initialized',
  'ongoing',
  'canceled_undone',
  'canceled_unpaid',
  'canceled_manual',
  'canceled_dispute_lost',
]);

/**
 * Affiché quand le contrat est en mode « factures Stripe » mais qu'aucune facture n'a pu
 * être remontée. Deux registres distincts :
 *  - information neutre quand le contrat n'a simplement pas encore produit de facture ;
 *  - alerte quand c'est un incident réel qui appelle une action.
 *
 * Propose en secours l'échéancier historique s'il en existe un réellement émis — sans quoi
 * une pièce comptable déjà transmise au bénéficiaire deviendrait inatteignable depuis la
 * fiche contrat.
 */
function StripeInvoicesUnavailable({
  errorCode,
  hasHistoricalEcheancier,
  onDownloadEcheancier,
  isDownloading,
  incidentTitle = 'Factures mensuelles indisponibles',
  pendingTitle = 'Factures mensuelles à venir',
}: {
  errorCode: string | null;
  hasHistoricalEcheancier: boolean;
  onDownloadEcheancier: () => void;
  isDownloading: boolean;
  /** Surchargés pour le comptant, qui n'a qu'une facture et rien de « mensuel ». */
  incidentTitle?: string;
  pendingTitle?: string;
}) {
  const isIncident = !!errorCode && !NON_ALARMING_ERRORS.has(errorCode);
  const label = errorCode
    ? STRIPE_INVOICES_ERROR_LABELS[errorCode]
    : 'Aucune facture mensuelle disponible pour le moment.';

  const tone = isIncident
    ? { box: 'tw-border-amber-200 tw-bg-amber-50', icon: 'tw-text-amber-600', text: 'tw-text-amber-800' }
    : { box: 'tw-border-gray-200 tw-bg-gray-50', icon: 'tw-text-gray-500', text: 'tw-text-gray-600' };

  return (
    <div className={`tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-rounded tw-border tw-p-2.5 ${tone.box}`}>
      {isIncident ? (
        <AlertTriangle className={`tw-w-4 tw-h-4 tw-shrink-0 tw-mt-0.5 ${tone.icon}`} />
      ) : (
        <Info className={`tw-w-4 tw-h-4 tw-shrink-0 tw-mt-0.5 ${tone.icon}`} />
      )}
      <div className={`tw-text-xs ${tone.text}`}>
        <p className="tw-font-medium tw-mb-0.5">
          {isIncident ? incidentTitle : pendingTitle}
        </p>
        <p className="tw-mb-0">{label}</p>
        {hasHistoricalEcheancier && (
          <button
            onClick={onDownloadEcheancier}
            disabled={isDownloading}
            className="tw-mt-1.5 tw-flex tw-items-center tw-gap-1.5 tw-font-medium tw-underline hover:tw-no-underline disabled:tw-opacity-50"
          >
            <Download className="tw-w-3.5 tw-h-3.5" />
            {isDownloading ? 'Téléchargement...' : 'Télécharger l\'échéancier d\'origine'}
          </button>
        )}
      </div>
    </div>
  );
}

function getProgressBarColor(percent: number, status: string): string {
  if (status === 'failed' || status === 'payment_failed_twice' || status === 'canceled_undone' || status === 'canceled_unpaid' || status === 'canceled_manual') {
    return 'tw-bg-red-500';
  }
  if (percent >= 100) return 'tw-bg-green-500';
  if (percent >= 50) return 'tw-bg-blue-500';
  return 'tw-bg-amber-500';
}

export function PaymentDetailsCard({ contractId, contractLabel, signatureType, societyId }: PaymentDetailsCardProps) {
  const { data: details, isLoading, error } = usePaymentDetails(contractId);
  const { download, isDownloading } = useDownloadInvoice(contractId, contractLabel, details?.paymentType === 2);
  const { download: downloadMandate, isDownloading: isDownloadingMandate } = useDownloadSepaMandate(contractId, contractLabel);
  const { data: effectiveConfig } = useEffectiveConfiguration(societyId);
  const queryClient = useQueryClient();
  const { currentUser, hasPermission } = useAuthStore();
  const isInternal = currentUser?.type === 'INTERNAL';
  // Report du 1er prélèvement : argent rendu au client, permission dédiée (hors `contract.*`).
  const canPostponeFirstCharge = isInternal && hasPermission('contract_payment.postpone_first_charge');
  const [isPostponeModalOpen, setIsPostponeModalOpen] = useState(false);
  const postponePreviewMutation = useFirstChargePostponePreview(contractId);
  const postponeConfirmMutation = useConfirmFirstChargePostpone(contractId);

  // Régénération facture échéancier (admin INTERNAL)
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const previewMutation = useInvoicePreview(contractId);
  const confirmMutation = useConfirmRegenerate(contractId);

  // Loading states
  const [isResending, setIsResending] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  // Direct payment modal state
  const [directPaymentLink, setDirectPaymentLink] = useState<string | null>(null);

  // Notification confirm dialog state
  const [notificationDialog, setNotificationDialog] = useState<{
    isOpen: boolean;
    title: string;
    action: 'resend' | 'updatePaymentMethod';
  }>({ isOpen: false, title: '', action: 'resend' });

  // Actions dropdown state
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!actionsOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionsOpen]);

  if (isLoading) {
    return (
      <div className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
          <Spinner animation="border" size="sm" />
          <span className="tw-ml-2 tw-text-sm tw-text-gray-500">Chargement des détails de paiement...</span>
        </div>
      </div>
    );
  }

  if (error || !details) return null;

  const statusInfo = PAYMENT_STATUS_LABELS[details.status] || PAYMENT_STATUS_LABELS.unknown;
  const isMonthly = details.paymentType === 2;
  const barColor = getProgressBarColor(details.progressPercent, details.status);
  const isPending = PENDING_STATUSES.includes(details.status);

  const openResendDialog = () => {
    setNotificationDialog({ isOpen: true, title: 'Renvoyer le lien de paiement', action: 'resend' });
  };

  const openUpdatePaymentMethodDialog = () => {
    setNotificationDialog({ isOpen: true, title: 'Envoyer un lien de mise à jour', action: 'updatePaymentMethod' });
  };

  const handleNotificationConfirm = async (email: string, phone: string) => {
    if (notificationDialog.action === 'resend') {
      setIsResending(true);
      try {
        const result = await contractApi.resendPaymentNotification(contractId, email, phone);
        if (result.success) {
          toast.success('Lien de paiement renvoyé avec succès');
        } else {
          toast.error(result.message || 'Échec de l\'envoi');
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Erreur lors de l\'envoi de la notification');
      } finally {
        setIsResending(false);
      }
    } else {
      setIsUpdatingPaymentMethod(true);
      try {
        const result = await contractApi.updatePaymentMethod(contractId, true, email, phone);
        if (result.success) {
          toast.success('Lien de mise à jour envoyé avec succès');
        } else {
          toast.error(result.message || 'Impossible d\'envoyer le lien de mise à jour');
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Erreur lors de l\'envoi du lien de mise à jour');
      } finally {
        setIsUpdatingPaymentMethod(false);
      }
    }
    setNotificationDialog({ isOpen: false, title: '', action: 'resend' });
  };

  const handleDirectPayment = async () => {
    setIsRegenerating(true);
    try {
      const result = await contractApi.regeneratePaymentLink(contractId);
      if (result.success && result.paymentLink) {
        setDirectPaymentLink(result.paymentLink);
      } else {
        toast.error(result.message || 'Impossible de générer le lien de paiement');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la génération du lien');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleUpdatePaymentMethodDirect = async () => {
    setIsUpdatingPaymentMethod(true);
    try {
      const result = await contractApi.updatePaymentMethod(contractId);
      if (result.success && result.paymentLink) {
        setDirectPaymentLink(result.paymentLink);
      } else {
        toast.error(result.message || 'Impossible de générer le lien de mise à jour');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la mise à jour du moyen de paiement');
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  const handleDirectPaymentSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['payment-details', contractId] });
    queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
    toast.success('Votre paiement a bien été enregistré, il se mettra à jour dans quelques instants.');
  };

  const handleOpenRegenerateInvoice = () => {
    setIsInvoiceModalOpen(true);
    previewMutation.mutate();
  };

  const handleOpenPostponeFirstCharge = () => {
    setIsPostponeModalOpen(true);
    postponePreviewMutation.mutate();
  };

  const handleClosePostponeFirstCharge = () => {
    if (postponeConfirmMutation.isLoading) return;
    setIsPostponeModalOpen(false);
    postponePreviewMutation.reset();
    postponeConfirmMutation.reset();
  };

  const handleConfirmPostponeFirstCharge = () => {
    postponeConfirmMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (data.eligible === false) {
          toast.error(data.reason);
          return;
        }
        toast.success(
          data.preview.payment
            ? 'Remboursement demandé, prélèvements à la date de début'
            : 'Prélèvements décalés à la date de début',
        );
        setIsPostponeModalOpen(false);
        postponePreviewMutation.reset();
        postponeConfirmMutation.reset();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || 'Erreur : les prélèvements n\'ont pas pu être décalés à la date de début');
      },
    });
  };

  const handleCloseRegenerateInvoice = () => {
    if (confirmMutation.isLoading) return;
    setIsInvoiceModalOpen(false);
    previewMutation.reset();
    confirmMutation.reset();
  };

  const handleConfirmRegenerateInvoice = () => {
    confirmMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (data.eligible === false) {
          toast.error(data.reason);
          return;
        }
        toast.success('Facture régénérée — le PDF a été mis à jour à la même URL');
        setIsInvoiceModalOpen(false);
        previewMutation.reset();
        confirmMutation.reset();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || 'Erreur lors de la régénération');
      },
    });
  };

  const postponePreview = postponePreviewMutation.data?.eligible ? postponePreviewMutation.data.preview : null;
  const postponePreviewError = postponePreviewMutation.error
    ? ((postponePreviewMutation.error as any)?.response?.data?.message ?? 'Erreur lors du chargement du récapitulatif')
    : postponePreviewMutation.data && postponePreviewMutation.data.eligible === false
      ? postponePreviewMutation.data.reason
      : null;

  const previewData =
    previewMutation.data?.eligible && previewMutation.data.regenerated === false
      ? previewMutation.data.preview
      : null;
  const previewErrorMessage = previewMutation.error
    ? ((previewMutation.error as any)?.response?.data?.message ?? 'Erreur lors du chargement du récapitulatif')
    : previewMutation.data && previewMutation.data.eligible === false
      ? previewMutation.data.reason
      : null;

  return (
    <>
      <div className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-p-4 tw-shadow-sm">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
          <div className="tw-flex tw-items-center tw-gap-2">
            <TrendingUp className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">
              {isMonthly ? 'Détails mensualisation' : 'Détails paiement'}
            </h3>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className={`tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
            {(isPending || (isMonthly && ['ongoing', 'succeeded', 'failed'].includes(details.status))) && (
              <div className="tw-relative" ref={actionsRef}>
                <button
                  onClick={() => setActionsOpen(!actionsOpen)}
                  className="tw-p-1.5 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors"
                >
                  <Settings className="tw-w-4 tw-h-4" />
                </button>
                {actionsOpen && (
                  <div className="tw-absolute tw-right-0 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-shadow-lg tw-z-10 tw-min-w-[240px] tw-py-1">
                    {isPending && (
                      <>
                        <button
                          onClick={() => { openResendDialog(); setActionsOpen(false); }}
                          disabled={isResending}
                          className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                        >
                          {isResending ? (
                            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin tw-text-gray-400 tw-flex-shrink-0" />
                          ) : (
                            <Mail className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
                          )}
                          Renvoyer le lien de paiement
                        </button>
                        {signatureType === 2 && (
                          <button
                            onClick={() => { handleDirectPayment(); setActionsOpen(false); }}
                            disabled={isRegenerating}
                            className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                          >
                            {isRegenerating ? (
                              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin tw-text-gray-400 tw-flex-shrink-0" />
                            ) : (
                              <CreditCard className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
                            )}
                            Paiement en direct
                          </button>
                        )}
                      </>
                    )}
                    {isMonthly && ['ongoing', 'succeeded', 'failed'].includes(details.status) && (
                      <>
                        <button
                          onClick={() => { openUpdatePaymentMethodDialog(); setActionsOpen(false); }}
                          disabled={isUpdatingPaymentMethod}
                          className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                        >
                          {isUpdatingPaymentMethod ? (
                            <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin tw-text-gray-400 tw-flex-shrink-0" />
                          ) : (
                            <Mail className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
                          )}
                          Envoyer un lien de mise à jour du moyen de paiement
                        </button>
                        {signatureType === 2 && (
                          <button
                            onClick={() => { handleUpdatePaymentMethodDirect(); setActionsOpen(false); }}
                            disabled={isUpdatingPaymentMethod}
                            className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                          >
                            {isUpdatingPaymentMethod ? (
                              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin tw-text-gray-400 tw-flex-shrink-0" />
                            ) : (
                              <RefreshCw className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
                            )}
                            Mise à jour en direct du moyen de paiement
                          </button>
                        )}
                        {canPostponeFirstCharge && (
                          <button
                            onClick={() => { handleOpenPostponeFirstCharge(); setActionsOpen(false); }}
                            className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-700 hover:tw-bg-amber-50 tw-transition-colors tw-border-t tw-border-gray-100"
                          >
                            <CalendarClock className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0" />
                            Rembourser et prélever à la date de début
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Barre de progression */}
        <div className="tw-mb-4">
          <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500 tw-mb-1">
            <span>
              {isMonthly
                ? `${details.paidCount} / ${details.totalCount} échéances`
                : details.paidCount > 0 ? 'Payé' : 'En attente'
              }
            </span>
            <span>{details.progressPercent}%</span>
          </div>
          <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2.5">
            <div
              className={`tw-h-2.5 tw-rounded-full tw-transition-all tw-duration-500 ${barColor}`}
              style={{ width: `${details.progressPercent}%` }}
            />
          </div>
        </div>

        {/* Montants en ligne */}
        <div className="tw-bg-gray-50 tw-rounded-md tw-p-3">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div className="tw-text-center">
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide">Montant payé</span>
              <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-mb-0">{formatPrice(details.paidAmount)}</p>
            </div>
            <div className="tw-text-center">
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide">Restant à payer</span>
              <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-mb-0">{formatPrice(details.remainingAmount)}</p>
            </div>
            <div className="tw-h-8 tw-w-px tw-bg-gray-300" />
            <div className="tw-text-center">
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide">Montant perçu</span>
              <p className="tw-text-sm tw-font-semibold tw-text-green-700 tw-mb-0">{formatPrice(details.receivedAmount)}</p>
            </div>
            <div className="tw-text-center">
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-tracking-wide">Restant à percevoir</span>
              <p className="tw-text-sm tw-font-semibold tw-text-green-700 tw-mb-0">{formatPrice(details.remainingToReceive)}</p>
            </div>
          </div>
        </div>

        {/* Détail des frais */}
        {details.totalFees > 0 && (
          <div className="tw-bg-gray-50 tw-rounded-lg tw-px-4 tw-py-3 tw-mt-3">
            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
              <span className="tw-text-gray-600">Frais de service</span>
              <span className="tw-font-medium tw-text-gray-900">{formatPrice(details.standardFees ?? details.totalFees)}</span>
            </div>
            {details.isUpsell && details.upsellFees !== undefined && details.upsellFees > 0 && (
              <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mt-1">
                <span className="tw-text-gray-600">Commission UpSell</span>
                <span className="tw-font-medium tw-text-indigo-700">{formatPrice(details.upsellFees)}</span>
              </div>
            )}
            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mt-1 tw-pt-1 tw-border-t tw-border-gray-200">
              <span className="tw-text-gray-600 tw-font-medium">Total frais</span>
              <span className="tw-font-bold tw-text-gray-900">{formatPrice(details.totalFees)}</span>
            </div>
          </div>
        )}

        <ContractDisputesBlock disputes={details.disputes} />

        {/*
          Le choix du bouton se fait sur le MODE du contrat (useStripeInvoicesForMonthly),
          jamais sur la présence de `stripeInvoices`. Se baser sur la présence des factures
          faisait retomber silencieusement sur l'échéancier au moindre incident Stripe :
          même libellé, même nom de fichier, aucun signal — le vendeur téléchargeait un
          échéancier en croyant obtenir ses factures mensuelles.
        */}
        {isMonthly && details.useStripeInvoicesForMonthly ? (
          details.stripeInvoices && details.stripeInvoices.length > 0 ? (
            <InvoicesDropdown invoices={details.stripeInvoices} />
          ) : (
            // Contrat pas encore prélevé, ou dans un état terminal : il n'y a rien à
            // annoncer. On n'affiche rien plutôt qu'un encart inquiétant sur un contrat
            // neuf, ou trompeur sur un contrat annulé.
            !NOTHING_TO_SHOW_STATUSES.has(details.status) && (
              <StripeInvoicesUnavailable
                errorCode={details.stripeInvoicesError}
                hasHistoricalEcheancier={details.hasHistoricalEcheancier}
                onDownloadEcheancier={download}
                isDownloading={isDownloading}
              />
            )
          )
        ) : !isMonthly && details.hasInvoice && !details.invoiceUrl && details.stripeInvoicesError ? (
          /*
            Comptant encaissé dont la facture Stripe est introuvable. Le bouton de
            téléchargement emprunte EXACTEMENT le même chemin de résolution que
            `invoiceUrl` : le proposer ici garantirait l'échec. On nomme l'incident.
            Aucun échéancier de secours : un comptant n'en a jamais eu.
          */
          <StripeInvoicesUnavailable
            errorCode={details.stripeInvoicesError}
            incidentTitle="Facture indisponible"
            pendingTitle="Facture à venir"
            hasHistoricalEcheancier={false}
            onDownloadEcheancier={download}
            isDownloading={isDownloading}
          />
        ) : (
          /* Mode échéancier (mensualisation) ou facture comptant */
          details.hasInvoice && (
            <div className="tw-mt-3">
              {details.invoiceUrl ? (
                <a
                  href={details.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium tw-transition-colors tw-no-underline"
                >
                  <Download className="tw-w-4 tw-h-4" />
                  Télécharger la facture
                </a>
              ) : (
                <button
                  onClick={download}
                  disabled={isDownloading}
                  className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium tw-transition-colors disabled:tw-opacity-50"
                >
                  <Download className="tw-w-4 tw-h-4" />
                  {isDownloading ? 'Téléchargement...' : isMonthly ? 'Télécharger l\'échéancier' : 'Télécharger la facture'}
                </button>
              )}
            </div>
          )
        )}

        {/* Bouton télécharger mandat SEPA client (mensualisation) */}
        {isMonthly && !!effectiveConfig?.clientMandateDownload && MANDATE_AVAILABLE_STATUSES.has(details.status) && (
          <div className="tw-mt-2">
            <button
              onClick={downloadMandate}
              disabled={isDownloadingMandate}
              className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium tw-transition-colors disabled:tw-opacity-50"
            >
              <FileSignature className="tw-w-4 tw-h-4" />
              {isDownloadingMandate ? 'Téléchargement...' : 'Télécharger le mandat de prélèvement'}
            </button>
          </div>
        )}

        {/* Bouton régénérer la facture échéancier (admin INTERNAL uniquement) */}
        {isInternal
          && isMonthly
          && details.invoiceType === 'echeancier'
          && details.hasInvoice
          && !details.useStripeInvoicesForMonthly && (
            <div className="tw-mt-2">
              <button
                onClick={handleOpenRegenerateInvoice}
                className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-amber-700 hover:tw-text-amber-800 tw-font-medium tw-transition-colors"
              >
                <RefreshCw className="tw-w-4 tw-h-4" />
                Régénérer l'échéancier
                <span className="tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700 tw-rounded">
                  Admin
                </span>
              </button>
            </div>
        )}
      </div>

      {/* Modale de paiement en direct */}
      {directPaymentLink && (
        <DirectPaymentModal
          isOpen={!!directPaymentLink}
          onClose={() => setDirectPaymentLink(null)}
          paymentLink={directPaymentLink}
          onPaymentSuccess={handleDirectPaymentSuccess}
          successMessage={isMonthly && ['ongoing', 'succeeded', 'failed'].includes(details.status)
            ? 'Renseignez les nouvelles coordonnées bancaires du bénéficiaire pour mettre à jour le moyen de paiement.'
            : 'Remplissez les informations de paiement du bénéficiaire pour procéder au règlement.'
          }
        />
      )}

      {/* Dialog de confirmation avant envoi de notification */}
      <SendNotificationConfirmDialog
        isOpen={notificationDialog.isOpen}
        title={notificationDialog.title}
        defaultEmail={details.beneficiaryEmail || ''}
        defaultPhone={details.beneficiaryPhone || ''}
        isLoading={isResending || isUpdatingPaymentMethod}
        onConfirm={handleNotificationConfirm}
        onCancel={() => setNotificationDialog({ isOpen: false, title: '', action: 'resend' })}
      />

      {/* Modale de régénération de la facture échéancier (admin INTERNAL) */}
      <RegenerateInvoiceModal
        isOpen={isInvoiceModalOpen}
        preview={previewData}
        isLoadingPreview={previewMutation.isLoading}
        previewError={previewErrorMessage}
        isConfirming={confirmMutation.isLoading}
        onClose={handleCloseRegenerateInvoice}
        onConfirm={handleConfirmRegenerateInvoice}
      />

      {/* Modale de report du 1er prélèvement (admin INTERNAL, permission dédiée) */}
      <PostponeFirstChargeModal
        isOpen={isPostponeModalOpen}
        preview={postponePreview}
        isLoadingPreview={postponePreviewMutation.isLoading}
        previewError={postponePreviewError}
        isConfirming={postponeConfirmMutation.isLoading}
        onClose={handleClosePostponeFirstCharge}
        onConfirm={handleConfirmPostponeFirstCharge}
      />
    </>
  );
}

function InvoicesDropdown({ invoices }: { invoices: Array<{ url: string; number: string; amount: number; date: string }> }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="tw-mt-3 tw-relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-font-medium tw-transition-colors"
      >
        <Download className="tw-w-4 tw-h-4" />
        Télécharger la facture ({invoices.length})
        <ChevronDown className={`tw-w-4 tw-h-4 tw-transition-transform ${isOpen ? 'tw-rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="tw-absolute tw-left-0 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-shadow-lg tw-z-10 tw-min-w-[320px] tw-max-h-60 tw-overflow-y-auto">
          {invoices.map((inv) => (
            <a
              key={inv.number}
              href={inv.url}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-no-underline tw-transition-colors"
            >
              <Download className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 tw-flex-shrink-0" />
              Facture {inv.number} — {formatPrice(inv.amount)} — {inv.date}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
